/**
 * 마이크 스트림 획득 (기본 장치 유실 / 에어팟 끊김 등 NotFoundError 대응)
 */

function isDomError(error: unknown, name: string): boolean {
  return (
    typeof error === "object" &&
    error !== null &&
    "name" in error &&
    (error as { name?: string }).name === name
  );
}

function toFriendlyMicError(error: unknown): Error {
  if (isDomError(error, "NotAllowedError") || isDomError(error, "PermissionDeniedError")) {
    return new Error("마이크 권한이 필요합니다. 브라우저 설정에서 마이크를 허용해 주세요.");
  }
  if (isDomError(error, "NotFoundError") || isDomError(error, "DevicesNotFoundError")) {
    return new Error(
      "마이크를 찾을 수 없습니다. 이어폰/마이크 연결을 확인하거나, 다른 오디오 입력 장치를 선택해 주세요."
    );
  }
  if (isDomError(error, "NotReadableError") || isDomError(error, "TrackStartError")) {
    return new Error(
      "마이크를 다른 앱이 사용 중이거나 장치에 접근할 수 없습니다. 다른 탭·앱의 녹음/통화를 종료한 뒤 다시 시도해 주세요."
    );
  }
  if (isDomError(error, "OverconstrainedError")) {
    return new Error("현재 기기에서 요청한 마이크 설정을 지원하지 않습니다. 다시 시도해 주세요.");
  }
  if (isDomError(error, "SecurityError")) {
    return new Error("보안 연결(HTTPS)에서만 마이크를 사용할 수 있습니다.");
  }
  if (error instanceof Error && error.message) {
    return error;
  }
  return new Error("마이크를 시작할 수 없습니다. 다시 시도해 주세요.");
}

async function tryGetUserMedia(
  constraints: MediaStreamConstraints
): Promise<MediaStream> {
  return navigator.mediaDevices.getUserMedia(constraints);
}

/**
 * 사용 가능한 마이크로 MediaStream을 가져옵니다.
 * Chrome에서 기본 장치(에어팟 등)가 사라진 뒤에도 deviceId가 남아
 * "Requested device not found"가 나는 경우를 재시도로 복구합니다.
 */
export async function getMicrophoneStream(): Promise<MediaStream> {
  if (typeof window === "undefined") {
    throw new Error("브라우저 환경에서만 녹음할 수 있습니다.");
  }
  if (!navigator.mediaDevices?.getUserMedia) {
    throw new Error("이 브라우저는 녹음을 지원하지 않습니다.");
  }

  const attempts: MediaStreamConstraints[] = [
    { audio: true },
    {
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
    },
    {
      audio: {
        echoCancellation: false,
        noiseSuppression: false,
        autoGainControl: false,
      },
    },
  ];

  let lastError: unknown;

  for (const constraints of attempts) {
    try {
      return await tryGetUserMedia(constraints);
    } catch (error) {
      lastError = error;
      // 권한 거부는 재시도해도 동일
      if (
        isDomError(error, "NotAllowedError") ||
        isDomError(error, "PermissionDeniedError")
      ) {
        throw toFriendlyMicError(error);
      }
    }
  }

  // 남아 있는 audioinput을 하나씩 시도 (끊긴 기본 장치 회피)
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    const mics = devices.filter(
      d => d.kind === "audioinput" && Boolean(d.deviceId)
    );

    for (const mic of mics) {
      try {
        return await tryGetUserMedia({
          audio: {
            deviceId: { ideal: mic.deviceId },
            echoCancellation: true,
          },
        });
      } catch (error) {
        lastError = error;
        if (
          isDomError(error, "NotAllowedError") ||
          isDomError(error, "PermissionDeniedError")
        ) {
          throw toFriendlyMicError(error);
        }
      }
    }

    if (mics.length === 0) {
      throw new Error(
        "연결된 마이크가 없습니다. 이어폰을 다시 꽂거나 시스템 소리 입력 장치를 확인해 주세요."
      );
    }
  } catch (error) {
    if (error instanceof Error && error.message.includes("마이크")) {
      throw error;
    }
    lastError = error;
  }

  throw toFriendlyMicError(lastError);
}
